import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, Moon, Sun, X } from 'lucide-react';
import { useSiteSettings } from '../../context/SiteSettingsContext';
import { useAuth } from '../../context/AuthContext';
import { useSiteFeatureFlags } from '../../hooks/useSiteFeatureFlags';
import { useAuthDrawer } from '../../context/AuthDrawerContext';
import { getConfig } from './api';

// Menu links may be site paths or full URLs (opened in a new tab).
const MenuLink = ({ to, children, ...props }) => (/^https?:\/\//i.test(to)
  ? <a href={to} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>
  : <Link to={to} {...props}>{children}</Link>);

// Light top bar for the transfer landing page. The logo comes from the same
// site settings the main Header reads (General Settings -> Site Information),
// so it is never set here.
const TransferTopBar = ({ dark, onToggleTheme, showThemeToggle = true, tone = 'overlay' }) => {
  const { logoUrl, siteName, logoHeight } = useSiteSettings();
  const { isAuthenticated } = useAuth();
  const { openAuth } = useAuthDrawer();
  const { showcase_enabled, events_enabled, transfer_site_enabled } = useSiteFeatureFlags();
  const [open, setOpen] = useState(false);
  const [siteMenu, setSiteMenu] = useState(null);

  // In the Transfer Site template the links are the ones set in Dev -> File Transfer.
  useEffect(() => {
    if (!transfer_site_enabled) return;
    getConfig().then((c) => setSiteMenu(c.site_menu)).catch(() => {});
  }, [transfer_site_enabled]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // Main public pages, mirroring what the site header offers (same flags).
  const flagLinks = [
    { to: '/about', label: 'About' },
    { to: '/tools', label: 'Free tools' },
    ...(showcase_enabled === true ? [{ to: '/apps', label: 'Portfolio' }] : []),
    ...(events_enabled ? [{ to: '/events', label: 'Events' }] : []),
    { to: '/contact', label: 'Support' },
  ];
  const links = transfer_site_enabled && siteMenu ? siteMenu.map((m) => ({ to: m.href, label: m.label })) : flagLinks;
  const account = isAuthenticated
    ? [{ to: '/account', label: 'My account', solid: true }]
    : [{ to: '/login', label: 'Log in', auth: 'login' }, { to: '/register', label: 'Sign up', solid: true, auth: 'register' }];
  // Account links open the slide-in drawer; the href stays for open-in-new-tab / no-JS.
  const onAccountClick = (l) => (e) => {
    if (!l.auth) return;
    e.preventDefault();
    setOpen(false);
    openAuth(l.auth);
  };

  const Logo = () => (
    <Link to="/" className="inline-flex items-center" data-testid="transfer-logo">
      {logoUrl ? (
        <img src={logoUrl} alt={siteName || 'Home'} style={{ height: `${logoHeight}px` }} className="w-auto object-contain" />
      ) : (
        <span className="text-xl font-bold">{siteName}</span>
      )}
    </Link>
  );

  // 'overlay' floats over the landing's imagery; 'bar' sits on a solid page bar.
  const pill = tone === 'bar'
    ? (dark ? 'bg-white/10 text-white' : 'bg-slate-100 text-slate-900')
    : (dark ? 'bg-black/60 text-white' : 'bg-white/85 text-slate-900');

  return (
    <header className="relative z-20">
      <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
        {/* No pill behind the logo: it sits straight on the page. The text colour follows the theme. */}
        <div className={`min-h-[48px] inline-flex items-center ${dark ? 'text-white' : 'text-slate-900'}`}><Logo /></div>

        <div className="flex items-center gap-2">
          <nav aria-label="Main" className={`${pill} backdrop-blur rounded-full px-2 hidden lg:flex items-center`}>
            {links.map((l) => (
              <MenuLink key={`${l.to}-${l.label}`} to={l.to} className="px-4 min-h-[48px] inline-flex items-center text-sm font-medium hover:opacity-70">
                {l.label}
              </MenuLink>
            ))}
          </nav>
          <nav aria-label="Account" className={`${pill} backdrop-blur rounded-full p-1 hidden lg:flex items-center`}>
            {account.map((l) => (
              <Link key={l.to} to={l.to} onClick={onAccountClick(l)}
                className={`px-4 min-h-[44px] inline-flex items-center text-sm font-medium rounded-full ${
                  l.solid ? (dark ? 'bg-white text-slate-900' : 'bg-slate-800 text-white') : 'hover:opacity-70'}`}>
                {l.label}
              </Link>
            ))}
          </nav>
          {showThemeToggle && (
            <button type="button" onClick={onToggleTheme} aria-label={dark ? 'Switch to light theme' : 'Switch to dark theme'}
              className={`${pill} backdrop-blur rounded-full w-12 h-12 inline-flex items-center justify-center`} data-testid="transfer-theme-toggle">
              {dark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
          )}
          <button type="button" onClick={() => setOpen(true)} aria-label="Open menu" aria-expanded={open}
            className={`${pill} backdrop-blur rounded-full w-12 h-12 inline-flex items-center justify-center lg:hidden`} data-testid="transfer-menu-button">
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Mobile menu: slides out from the right, logo at the top. */}
      <div onClick={() => setOpen(false)} aria-hidden="true"
        className={`lg:hidden fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} />
      <aside aria-label="Menu" aria-hidden={!open}
        className={`lg:hidden fixed inset-y-0 right-0 z-50 w-[85vw] max-w-sm shadow-2xl transition-transform duration-300 ease-in-out ${
          dark ? 'bg-slate-900 text-white' : 'bg-white text-slate-900'} ${open ? 'translate-x-0' : 'translate-x-full'}`}
        data-testid="transfer-menu">
        <div className="flex items-center justify-between px-4 py-4 border-b border-current/10">
          <Logo />
          <button type="button" onClick={() => setOpen(false)} aria-label="Close menu" className="w-12 h-12 inline-flex items-center justify-center">
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav aria-label="Mobile" className="p-4 flex flex-col">
          {[...links, ...account].map((l) => (
            <MenuLink key={`${l.to}-${l.label}`} to={l.to} onClick={(e) => { setOpen(false); onAccountClick(l)(e); }} tabIndex={open ? 0 : -1}
              className="min-h-[48px] inline-flex items-center text-lg font-medium">
              {l.label}
            </MenuLink>
          ))}
        </nav>
      </aside>
    </header>
  );
};

export default TransferTopBar;
